/**
 * Phase 10 R5 — useShopFinancialSettings
 *
 * Reads + updates the 5 shop-level financial control columns:
 *   - financial_visibility_mode
 *   - target_parts_gp_percent
 *   - target_labor_gp_percent
 *   - target_total_gp_percent
 *   - low_margin_warn_threshold
 *
 * The mutation is gated by role (OWNER/MANAGER) at the call site via
 * `canEditFinancialSettings`. This hook itself is pure data plumbing.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { FinancialVisibilityMode } from '@/lib/labor/resolveFinancialVisibility';

export interface ShopFinancialSettings {
  financial_visibility_mode: FinancialVisibilityMode;
  target_parts_gp_percent: number | null;
  target_labor_gp_percent: number | null;
  target_total_gp_percent: number | null;
  low_margin_warn_threshold: number;
}

export type ShopFinancialSettingsPatch = Partial<ShopFinancialSettings>;

const DEFAULTS: ShopFinancialSettings = {
  financial_visibility_mode: 'owner_manager_only',
  target_parts_gp_percent: null,
  target_labor_gp_percent: null,
  target_total_gp_percent: null,
  low_margin_warn_threshold: 10,
};

const settingsKey = (shopId: string) =>
  ['shop-financial-settings', shopId] as const;

export function useShopFinancialSettings() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  const query = useQuery({
    queryKey: settingsKey(shopId ?? ''),
    enabled: !!shopId,
    queryFn: async (): Promise<ShopFinancialSettings> => {
      const { data, error } = await (supabase as any)
        .from('shops')
        .select(
          'financial_visibility_mode, target_parts_gp_percent, target_labor_gp_percent, target_total_gp_percent, low_margin_warn_threshold',
        )
        .eq('id', shopId)
        .single();
      if (error) throw error;
      return {
        financial_visibility_mode:
          (data?.financial_visibility_mode as FinancialVisibilityMode) ??
          DEFAULTS.financial_visibility_mode,
        target_parts_gp_percent:
          data?.target_parts_gp_percent != null
            ? Number(data.target_parts_gp_percent)
            : null,
        target_labor_gp_percent:
          data?.target_labor_gp_percent != null
            ? Number(data.target_labor_gp_percent)
            : null,
        target_total_gp_percent:
          data?.target_total_gp_percent != null
            ? Number(data.target_total_gp_percent)
            : null,
        low_margin_warn_threshold:
          data?.low_margin_warn_threshold != null
            ? Number(data.low_margin_warn_threshold)
            : DEFAULTS.low_margin_warn_threshold,
      };
    },
  });

  return { ...query, settings: query.data ?? DEFAULTS };
}

export function useUpdateShopFinancialSettings() {
  const qc = useQueryClient();
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useMutation({
    mutationFn: async (patch: ShopFinancialSettingsPatch) => {
      if (!shopId) throw new Error('No active shop');
      const { error } = await (supabase as any)
        .from('shops')
        .update(patch)
        .eq('id', shopId);
      if (error) throw error;
      return patch;
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: settingsKey(shopId) });
      }
    },
  });
}
